<template>
  <div v-if="shouldShowControls" class="grid grid-cols-2 gap-4 self-stretch">
    <PixelNumber
      :label="t('common.style.imageFieldSpacing')"
      :value="currentImageFieldSpacing"
      :max="1800"
      @update:value="handleImageFieldSpacing"
    />
    <PixelNumber
      :label="t('common.style.imageCornerRadius')"
      :value="currentImageCornerRadius"
      :max="1800"
      @update:value="handleImageCornerRadius"
    />
  </div>
</template>

<script setup lang="ts">
import { useAppStore } from "@/store/app";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import PixelNumber from "./PixelNumber.vue";

const { t } = useI18n();
const store = useAppStore();

// Props
const props = defineProps<{
  isSurvey?: boolean;
  isEditingSurveyDefault?: boolean;
  selectedSlideIndex?: number;
  desktopImageLayout?: string;
  mobileImageLayout?: string;
  showImage?: boolean;
  imageFieldSpacing?: number;
  imageCornerRadius?: number;
}>();

// Emits
const emit = defineEmits<{
  "update:imageFieldSpacing": [value: number];
  "update:imageCornerRadius": [value: number];
}>();

/**
 * @description Determines if controls should be visible based on layout type
 * Controls only show for leftCenter and rightCenter layouts
 */
const shouldShowControls = computed(() => {
  const layout = props.isSurvey
    ? store.deviceMode === "mobile"
      ? props.mobileImageLayout
      : props.desktopImageLayout
    : store.deviceMode === "mobile"
    ? store.builderConfig.mobileImageLayout
    : store.builderConfig.desktopImageLayout;

  const showImage = props.isSurvey ? props.showImage : store.builderConfig.showImage;

  return showImage && ["leftCenter", "rightCenter"].includes(layout || "");
});

/**
 * @description Gets the current image field spacing value
 */
const currentImageFieldSpacing = computed(() => {
  if (props.isSurvey) {
    if (props.isEditingSurveyDefault) {
      return store.surveyImageSettings.surveyDefault.imageFieldSpacing ?? 24;
    }
    // For slide-level settings, use the passed prop
    return props.imageFieldSpacing ?? 24;
  }
  return store.builderConfig.imageFieldSpacing ?? 24;
});

/**
 * @description Gets the current image corner radius value
 */
const currentImageCornerRadius = computed(() => {
  if (props.isSurvey) {
    if (props.isEditingSurveyDefault) {
      return store.surveyImageSettings.surveyDefault.imageCornerRadius ?? 0;
    }
    // For slide-level settings, use the passed prop
    return props.imageCornerRadius ?? 0;
  }
  return store.builderConfig.imageCornerRadius ?? 0;
});

/**
 * @description Handles image field spacing update
 * @param val - The new spacing value
 */
const handleImageFieldSpacing = (val: number) => {
  if (props.isSurvey) {
    emit("update:imageFieldSpacing", val);
  } else {
    store.builderConfig.imageFieldSpacing = val;
    store.anyUnsavedChanges = true;
  }
};

/**
 * @description Handles image corner radius update
 * @param val - The new corner radius value
 */
const handleImageCornerRadius = (val: number) => {
  if (props.isSurvey) {
    emit("update:imageCornerRadius", val);
  } else {
    store.builderConfig.imageCornerRadius = val;
    store.anyUnsavedChanges = true;
  }
};
</script>
